/**
 * ConversorPcaPage — PDF do Plano de Contratações Anual → JSON (F11).
 *
 * Réplica do super_admin/pca_converter.html. Três passos: escolher a
 * prefeitura e o exercício, enviar o PDF (a extração roda no servidor, com
 * pdfplumber + Gemini) e gravar.
 *
 * Um PCA por prefeitura/ano: reenviar SUBSTITUI o do mesmo exercício — é o
 * plano daquele exercício, não uma coleção de versões.
 */

import {
  faCheckCircle,
  faCloudUploadAlt,
  faCopy,
  faDownload,
  faExclamationTriangle,
  faFileInvoiceDollar,
  faFilePdf,
  faSpinner,
  faTrashAlt,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import {
  excluirPca,
  extrairPca,
  painelSuperAdmin,
  pcasSalvos,
  salvarPca,
} from '../../api/superAdmin'
import { useToast } from '../../components/Toast'

const CAMPO =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100'

interface Metadados {
  arquivo?: string
  paginas?: number
  tabelas_encontradas?: number
  caracteres_processados?: number
}

export function ConversorPcaPage() {
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const entrada = useRef<HTMLInputElement>(null)

  const [tenantId, setTenantId] = useState('')
  const [ano, setAno] = useState(String(new Date().getFullYear()))
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [extraido, setExtraido] = useState<Record<string, unknown> | null>(null)
  const [metadados, setMetadados] = useState<Metadados>({})
  const [erro, setErro] = useState('')

  const { data: painel } = useQuery({
    queryKey: ['super-admin', 'painel'],
    queryFn: painelSuperAdmin,
  })
  const { data: salvos } = useQuery({
    queryKey: ['super-admin', 'pcas'],
    queryFn: pcasSalvos,
  })

  const extrair = useMutation({
    mutationFn: () => extrairPca(arquivo as File),
    onSuccess: (r) => {
      setExtraido(r.data)
      setMetadados(r.metadata as Metadados)
      setErro('')
      notificar('success', 'Extração concluída. Confira e grave.')
    },
    onError: (e: Error) => {
      setErro(e.message)
      notificar('error', e.message)
    },
  })

  const gravar = useMutation({
    mutationFn: () => {
      const json = { ...(extraido ?? {}) } as Record<string, unknown>
      const documento = { ...((json.documento as object) ?? {}) } as Record<
        string,
        unknown
      >
      // o ano do formulário manda: o extrator às vezes lê o ano da capa
      documento.ano_exercicio = Number(ano)
      json.documento = documento
      return salvarPca(Number(tenantId), json, metadados as Record<string, unknown>)
    },
    onSuccess: (r) => {
      notificar(
        'success',
        `PCA ${r.situacao} para ${r.ano} — ${r.itens} item(ns).`,
      )
      void clienteQuery.invalidateQueries({ queryKey: ['super-admin', 'pcas'] })
    },
    onError: (e: Error) => {
      setErro(e.message)
      notificar('error', e.message)
    },
  })

  const excluir = useMutation({
    mutationFn: (id: number) => excluirPca(id),
    onSuccess: () => {
      notificar('success', 'PCA excluído.')
      void clienteQuery.invalidateQueries({ queryKey: ['super-admin', 'pcas'] })
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const json = extraido ? JSON.stringify(extraido, null, 2) : ''
  const itens =
    (extraido?.itens as unknown[] | undefined) ??
    (extraido?.contratacoes as unknown[] | undefined) ??
    []

  function baixar() {
    const url = URL.createObjectURL(
      new Blob([json], { type: 'application/json' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = `pca-${ano}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-purple-700 via-purple-800 to-indigo-900 p-6 text-white">
          <h1 className="flex items-center gap-3 text-2xl font-bold">
            <FontAwesomeIcon icon={faFileInvoiceDollar} />
            Conversor de PCA
          </h1>
          <p className="mt-1 text-purple-200">
            O PDF do Plano de Contratações Anual vira JSON e passa a alimentar
            o formulário do DFD (art. 12, VII da Lei 14.133/2021).
          </p>
        </div>
      </header>

      {erro && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
          <span>{erro}</span>
        </div>
      )}

      <section className="mb-6 grid grid-cols-1 gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card md:grid-cols-2">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Prefeitura <span className="text-red-500">*</span>
          <select
            id="prefeitura"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className={`mt-1 ${CAMPO}`}
          >
            <option value="">Selecione…</option>
            {(painel?.prefeituras ?? [])
              .filter((p) => p.ativa)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.municipio} — {p.orgao}
                </option>
              ))}
          </select>
        </label>
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Ano do exercício
          <input
            id="ano"
            type="number"
            min={2020}
            max={2035}
            value={ano}
            onChange={(e) => setAno(e.target.value)}
            className={`mt-1 ${CAMPO}`}
          />
        </label>
      </section>

      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="rounded-2xl border-2 border-dashed border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50 p-8 text-center dark:border-purple-700 dark:from-purple-900/20 dark:to-indigo-900/20">
          <FontAwesomeIcon
            icon={arquivo ? faFilePdf : faCloudUploadAlt}
            className="mb-3 text-3xl text-purple-500"
          />
          <div>
            <button
              type="button"
              onClick={() => entrada.current?.click()}
              className="text-lg font-semibold text-purple-600 hover:text-purple-700 dark:text-purple-400"
            >
              {arquivo ? arquivo.name : 'Escolher o PDF do PCA'}
            </button>
            <input
              ref={entrada}
              id="pdf"
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={(e) => {
                setArquivo(e.target.files?.[0] ?? null)
                setExtraido(null)
                e.target.value = ''
              }}
            />
          </div>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            A leitura roda no servidor e pode levar até dois minutos.
          </p>
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            disabled={!arquivo || extrair.isPending}
            onClick={() => extrair.mutate()}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={extrair.isPending ? faSpinner : faWandMagicSparkles}
              spin={extrair.isPending}
            />
            {extrair.isPending ? 'Processando…' : 'Processar com IA'}
          </button>
          <button
            type="button"
            disabled={!extraido || !tenantId || gravar.isPending}
            onClick={() => gravar.mutate()}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-3 font-semibold text-white shadow-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={gravar.isPending ? faSpinner : faCheckCircle}
              spin={gravar.isPending}
            />
            Salvar no banco
          </button>
          <button
            type="button"
            disabled={!json}
            onClick={() => {
              void navigator.clipboard.writeText(json)
              notificar('success', 'JSON copiado.')
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-6 py-3 font-medium text-slate-600 disabled:opacity-40 dark:border-slate-600 dark:text-slate-300"
          >
            <FontAwesomeIcon icon={faCopy} />
            Copiar JSON
          </button>
          <button
            type="button"
            disabled={!json}
            onClick={baixar}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-6 py-3 font-medium text-slate-600 disabled:opacity-40 dark:border-slate-600 dark:text-slate-300"
          >
            <FontAwesomeIcon icon={faDownload} />
            Baixar JSON
          </button>
        </div>
      </section>

      {extraido && (
        <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-4">
            {[
              ['Páginas', String(metadados.paginas ?? '—')],
              ['Tabelas', String(metadados.tabelas_encontradas ?? '—')],
              ['Caracteres', String(metadados.caracteres_processados ?? '—')],
              ['Itens no plano', String(itens.length)],
            ].map(([rotulo, valor]) => (
              <div
                key={rotulo}
                className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"
              >
                <p className="text-xs uppercase text-slate-500">{rotulo}</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {valor}
                </p>
              </div>
            ))}
          </div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            JSON extraído
            <textarea
              readOnly
              value={json}
              rows={16}
              className={`mt-1 font-mono text-xs ${CAMPO}`}
            />
          </label>
        </section>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
          PCAs no sistema
        </h2>
        {(salvos?.pcas ?? []).length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600">
            Nenhum PCA gravado ainda.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-600">
                  <th className="py-2 pr-4">Exercício</th>
                  <th className="py-2 pr-4">Prefeitura</th>
                  <th className="py-2 pr-4">Título</th>
                  <th className="py-2 pr-4">Total</th>
                  <th className="py-2 pr-4">Origem</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {(salvos?.pcas ?? []).map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-slate-100 dark:border-slate-700"
                  >
                    <td className="py-2 pr-4 font-medium text-slate-800 dark:text-slate-100">
                      {p.ano}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                      {p.prefeitura}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                      {p.titulo}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 dark:text-slate-400">
                      {p.total}
                    </td>
                    <td className="py-2 pr-4 text-xs text-slate-500">
                      {p.arquivo || '—'}
                      <span className="block">{p.atualizado_em}</span>
                    </td>
                    <td className="py-2">
                      <button
                        type="button"
                        aria-label={`Excluir PCA ${p.ano} de ${p.prefeitura}`}
                        onClick={() => {
                          if (confirm(`Excluir o PCA ${p.ano} de ${p.prefeitura}?`))
                            excluir.mutate(p.id)
                        }}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-red-600 hover:bg-red-50 dark:border-slate-600"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
